import {
  Autocomplete,
  Chip,
  CircularProgress,
  FormControl,
  TextField,
  IconButton,
  Box,
  InputAdornment,
} from '@mui/material';
import { isEqual } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import FormFieldWrapper from './FormFieldWrapper';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { MAIN_THEME } from '@styles';
import { IFormFieldInputBaseProps } from '.';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Clear } from '@mui/icons-material';
import { isEmpty } from '@utils/validationUtils';
import { IPagination } from '@models';

export interface IAutocompleteFormFieldProps
  extends IFormFieldInputBaseProps {
  value: string;
  loader?: boolean;
  freeSolo?: boolean;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  classes?: { input?: string; wrapper?: string };
  maxItemsDisplayed?: number;
  canClearValue?: boolean;
  initialOptions?: {
    options: Record<string, string>;
    totalCount: number;
  };
  fetchOptions: (
    query: string,
    pagination: IPagination,
    hasDefaultValue?: boolean
  ) => Promise<{
    options: Record<string, string>;
    totalCount: number;
  }>;
}

const useStyles = makeStyles<{
  error?: string;
}>()((theme, { error }) => ({
  inputContainerStyles: {
    width: '100%',
  },
  autocomplete: {
    // height: '34px',
  },
  inputStyles: {
    width: '100%',
    border: `1px solid ${MAIN_THEME.color.white86}`,
    borderRadius: '5px',
    outline: 'none',
    '& .MuiInputBase-input': {
      fontFamily: MAIN_THEME.font.regular,
      '&::placeholder': {
        fontStyle: 'italic',
        fontFamily: MAIN_THEME.font.regular,
        color: MAIN_THEME.color.white86,
        opacity: 1,
        fontSize: MAIN_THEME.fontSize.sm,
      },
    },
    '& .MuiInputBase-root': {
      fontSize: MAIN_THEME.fontSize.sm,
      lineHeight: '15px',
      minHeight: '35px',
      width: '100%',
      padding: '0 25px 0 15px!important',
      margin: '0 auto',
      backgroundColor: MAIN_THEME.color.white98,
      outlineColor: error ? theme.palette.error.main : undefined,
      border: error
        ? `1px solid ${theme.palette.error.main}`
        : `0px solid ${MAIN_THEME.color.white92}`,
      boxSizing: 'border-box',
      borderRadius: '5px',
      '&::before': {
        display: 'none',
      },
      '&::after': {
        display: 'none',
      },
    },
  },
  optionDesign: {
    fontSize: `${MAIN_THEME.fontSize.sm}`,
    lineHeight: '15px',
    color: MAIN_THEME.color.black,
    fontFamily: MAIN_THEME.font.regular,
  },
  paginationIcons: {
    display: 'flex',
    gap: '5px',
    justifyContent: 'end',
    alignItems: 'center',
    fontSize: MAIN_THEME.fontSize.sm,
    padding: '5px 7px 0 0',
    '& .MuiSvgIcon-root': {
      width: '0.8em',
    },
    '& .MuiIconButton-root': {
      padding: '2px',
    },
  },
  loader: {
    position: 'absolute',
    top: 'calc(50% - 12px)',
    right: '6%',
    opacity: 1,
    height: '25px',
    width: '25px',
  },
  selectMaterial: {
    '& .MuiInput-input': {
      fontSize: MAIN_THEME.fontSize.sm,
      padding: '7px 4px 0 0 !important',
      color: `${MAIN_THEME.color.black}`,
      '&::placeholder': {
        fontStyle: 'italic',
        fontFamily: MAIN_THEME.font.regular,
        color: MAIN_THEME.color.white86,
        opacity: 1,
        fontSize: MAIN_THEME.fontSize.sm,
      },
    },
    '& .MuiInputBase-root': {
      height: '34.5px',
    },
    '& .Mui-disabled:before': {
      borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
    },
    '& .MuiInputBase-root:before': {
      border: 'none',
      backgroundColor: 'unset',
      borderRadius: '0',
      borderBottom: `1px solid ${MAIN_THEME.color.white92}`,
    },

    '& .MuiInput-underline:hover:not(.Mui-disabled):before, & .MuiInputBase-root:after':
      {
        borderBottom: `1px solid ${MAIN_THEME.color.white92}`,
      },
  },
  selectError: {
    '& .MuiInputBase-root:before': {
      borderBottom: `1px solid ${theme.palette.error.main}`,
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before, & .MuiInputBase-root:after':
      {
        borderBottom: `1px solid ${theme.palette.error.main}`,
      },
    '& .MuiInputBase-root:focus': {
      borderColor: theme.palette.error.main,
      outlineColor: theme.palette.error.main,
      boxShadow: 'none',
      borderRadius: 0,
    },
    '& .MuiInputBase-root': {
      borderColor: `${theme.palette.error.main}`,
      outlineColor: `${theme.palette.error.main}`,
    },
  },
  clearBtnContainer: {
    marginRight: '10px',
  },
  clearBtn: {
    cursor: 'pointer',
    height: '12px',
    width: '12px',
  },
}));

export interface IAutocompletePagination {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
}

function getPaginationRange(
  pageNumber: number,
  pageSize: number,
  totalCount: number
) {
  const from = (pageNumber - 1) * pageSize + 1;
  const to = Math.min(pageNumber * pageSize, totalCount);
  return `${from} – ${to} of ${totalCount}`;
}

/**
 * This is a custom Autocomplete component that:
 * 1. Fetches options from a remote source with pagination.
 * 2. Debounces API calls when the user types (only fires after 3+ chars).
 * 3. Shows pagination controls ("Next" and "Previous") in the dropdown.
 * 4. Clears the selection if the user types an invalid option and blurs.
 */
const AutocompleteFormField: React.FC<IAutocompleteFormFieldProps> = ({
  name,
  title,
  placeholder,
  className,
  style,
  classes = {},
  value,
  error,
  onChange,
  onBlur,
  disabled,
  loader,
  inputOnly,
  freeSolo = true,
  material,
  maxItemsDisplayed = 10,
  fetchOptions,
  canClearValue = false,
  //needed in case options change after load
  initialOptions,
}) => {
  const { classes: inputClasses } = useStyles({ error });
  const hasDefaultValue = useRef<boolean>(!!value);
  const [options, setOptions] = useState<Array<{ id: string; label: string }>>(
    transformToOptionsArray(initialOptions?.options) || []
  );
  // e.g. if parent gives us `value="123"`, let's find the matching option:
  const selectedOption = options.find((o) => o.id === value) || {
    id: '',
    label: '',
  };
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<IAutocompletePagination>({
    pageNumber: 1,
    pageSize: maxItemsDisplayed,
    totalCount: 0,
  });
  /**
   * searchTerm holds whatever the user has typed, which is used for
   * debounced fetch calls. This is separate from "value", the current selection.
   */
  const [searchTerm, setSearchTerm] = useState<string>(
    selectedOption?.label || value || ''
  );
  // A ref that tells us to skip the next fetch
  const skipNextFetchRef = useRef(false);

  // Update options if "initialOptions" changes
  useEffect(() => {
    if (
      !initialOptions ||
      isEqual(transformToOptionsArray(initialOptions?.options), options)
    )
      return;
    const newOptions = transformToOptionsArray(initialOptions?.options);
    setOptions(newOptions);
    setPagination((oldPagination) => ({
      ...oldPagination,
      totalCount: initialOptions.totalCount,
    }));
  }, [initialOptions]);

  const getOptions = useCallback(
    (value: string, paginationValues: IAutocompletePagination) => {
      setLoading(true);
      fetchOptions(
        value,
        {
          pageSize: paginationValues.pageSize,
          pageNumber: paginationValues.pageNumber,
        },
        hasDefaultValue.current
      )
        .then(({ options, totalCount }) => {
          const newOptions = transformToOptionsArray(options);
          setOptions(newOptions);
          setPagination({
            ...paginationValues,
            totalCount,
          });
          const matchingOption = newOptions.find((o) => o.id === value);
          if (matchingOption && matchingOption.label !== searchTerm) {
            // We are about to set searchTerm => label.
            // So skip the next effect-based fetch.
            skipNextFetchRef.current = true;
            // Only set searchTerm if the label is different
            setSearchTerm(matchingOption.label);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },

    []
  );

  // DEBOUNCING: watch `searchTerm` and call getOptions after a delay
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    // If we decided to skip the next fetch, do so now
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
    }
    // If the search is empty, fetch an empty list (or default)
    else if (searchTerm?.length === 0) {
      getOptions('', {
        pageNumber: 1,
        pageSize: maxItemsDisplayed,
        totalCount: pagination.totalCount,
      });
    }
    // If < 3 characters, do nothing (no fetch)
    else if (searchTerm?.length < 2) {
      // do nothing
    }
    // Otherwise, set a debounced timer for the fetch
    else {
      timer = setTimeout(() => {
        getOptions(searchTerm || value, {
          pageNumber: 1,
          pageSize: maxItemsDisplayed,
          totalCount: pagination.totalCount,
        });
      }, 400);
    }

    // Always return a cleanup function - if timer is null, it does nothing
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [searchTerm]);

  const handleClearValue = () => {
    onChange?.('');
  };

  const renderInput = () => {
    const optionsLength = options?.length;

    return (
      <div className={inputClasses.inputContainerStyles}>
        <FormControl
          error={!!error}
          disabled={!!disabled}
          className={inputClasses.inputContainerStyles}
          size="small"
          variant="outlined"
        >
          <Autocomplete
            // freeSolo is commented because we want to ensure the value
            freeSolo={freeSolo}
            // forcePopupIcon ensures the dropdown arrow shows even if freeSolo
            forcePopupIcon
            multiple={false}
            disabled={!!disabled}
            disablePortal={false}
            options={options}
            isOptionEqualToValue={(option, val) => {
              // MUI uses this to determine the selected item
              return option.id === val.id;
            }}
            onInputChange={(event, newInputValue, reason) => {
              // We clear the skip ref so next effect won't skip
              if (reason === 'input') {
                // Actual typing
                skipNextFetchRef.current = false;
                hasDefaultValue.current = false;
                setSearchTerm(newInputValue);
                setPagination((oldPagination) => ({
                  ...oldPagination,
                  pageNumber: 1,
                }));
              } else if (reason === 'clear') {
                skipNextFetchRef.current = false;
                hasDefaultValue.current = false;
                // Clear button used
                setSearchTerm('');
                onChange?.(''); // Clear selection
                setPagination((oldPagination) => ({
                  ...oldPagination,
                  pageNumber: 1,
                }));
              }
              // If "reset", do nothing => avoids an API call on dropdown selection
            }}
            onBlur={() => {
              // If invalid or no options, clear
              if (!options?.length || !selectedOption?.id) {
                setSearchTerm('');
                onChange?.('');
                setPagination((oldPagination) => ({
                  ...oldPagination,
                  pageNumber: 1,
                }));
              }
              onBlur && onBlur(value);
            }}
            onChange={(event, value) => {
              skipNextFetchRef.current = true;
              if (typeof value === 'object' && value.id) {
                onChange?.(value.id);
                setSearchTerm(value.label);
              }
              // If freeSolo typed text (a string)
              else if (typeof value === 'string') {
                onChange?.(value);
                setSearchTerm(value);
              }
            }}
            // Selected item must be the object
            value={selectedOption}
            // The text displayed in the input
            inputValue={searchTerm}
            // MUI uses label from the object
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              else return option.label;
            }}
            disableClearable={true}
            selectOnFocus
            loading={loading}
            className={inputClasses.autocomplete}
            noOptionsText={loading ? 'Fetching data...' : 'No data'}
            // This is where we inject pagination buttons
            renderOption={(props, option, state) => {
              const showPagination =
                optionsLength < pagination.totalCount &&
                state.index === optionsLength - 1;
              return (
                <>
                  <li {...props} key={option.id}>
                    <span className={inputClasses.optionDesign}>
                      {option.label}
                    </span>
                  </li>
                  {showPagination && (
                    <Box className={inputClasses.paginationIcons}>
                      {getPaginationRange(
                        pagination.pageNumber,
                        pagination.pageSize,
                        pagination.totalCount
                      )}
                      <IconButton
                        size="small"
                        disabled={pagination.pageNumber === 1 || loading}
                        onClick={(e) => {
                          e.stopPropagation();
                          const pageNumber = Math.max(
                            pagination.pageNumber - 1,
                            1
                          );
                          getOptions(searchTerm, {
                            pageSize: pagination.pageSize,
                            pageNumber,
                            totalCount: pagination.totalCount,
                          });
                        }}
                      >
                        <NavigateBeforeIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={loading}
                        onClick={(e) => {
                          e.stopPropagation();
                          const pageNumber = pagination.pageNumber + 1;
                          getOptions(searchTerm, {
                            pageSize: pagination.pageSize,
                            pageNumber,
                            totalCount: pagination.totalCount,
                          });
                        }}
                      >
                        <NavigateNextIcon />
                      </IconButton>
                    </Box>
                  )}
                </>
              );
            }}
            renderTags={(value, getTagProps) =>
              Array.isArray(value) &&
              value.map(
                (
                  { id, label }: { id: string; label: string },
                  index: number
                ) =>
                  !isEmpty(label) ? (
                    <Chip
                      variant="filled"
                      label={label}
                      style={{
                        height: 'unset',
                        color: MAIN_THEME.color.white,
                        backgroundColor: MAIN_THEME.color.black25,
                        borderRadius: '0px',
                      }}
                      {...getTagProps({ index })}
                      key={id}
                    />
                  ) : (
                    <Chip
                      variant="filled"
                      style={{
                        height: 'unset',
                        color: MAIN_THEME.color.white,
                        backgroundColor: MAIN_THEME.color.black25,
                        borderRadius: '0px',
                      }}
                      label={label}
                      {...getTagProps({ index })}
                      key={id}
                    />
                  )
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                name={name}
                placeholder={placeholder}
                className={clsx(
                  !material && inputClasses.inputStyles,
                  material && inputClasses.selectMaterial,
                  error && inputClasses.selectError
                )}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: params.InputProps.startAdornment,
                  endAdornment: (
                    <>
                      {value && canClearValue && (
                        <InputAdornment
                          position="end"
                          className={inputClasses.clearBtnContainer}
                        >
                          <IconButton
                            aria-label="clear date"
                            onClick={handleClearValue}
                            edge="end"
                            size="small"
                            style={{ marginRight: material ? '30px' : '-17px' }}
                          >
                            <Clear className={inputClasses.clearBtn} />
                          </IconButton>
                        </InputAdornment>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                variant="standard"
              />
            )}
          />
        </FormControl>
      </div>
    );
  };

  return inputOnly ? (
    renderInput()
  ) : (
    <>
      <FormFieldWrapper
        title={title}
        error={error}
        name={name}
        className={clsx(classes.wrapper, className)}
        style={style}
        material={material}
      >
        {renderInput()}
        {loader && (
          <CircularProgress
            className={inputClasses.loader}
            size={20}
            thickness={3}
          />
        )}
      </FormFieldWrapper>
    </>
  );
};

export default AutocompleteFormField;

function transformToOptionsArray(options: Record<string, string>) {
  return (
    Object.keys(options || {})?.map((key) => ({
      id: key,
      label: options[key],
    })) || []
  );
}
